import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { 
  LayoutDashboard, Building2, Bed, ClipboardList, FileText, Users, LogOut, Menu, X, 
  ChevronRight, Clock, CheckCircle2, AlertCircle, Plus, Download, Search, Filter, RefreshCw,
  Pencil, Trash2
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './components/ui/dialog';
import { Checkbox } from './components/ui/checkbox';
import { Textarea } from './components/ui/textarea';
import { Separator } from './components/ui/separator';
import { ScrollArea } from './components/ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from './components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Progress } from './components/ui/progress';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './components/ui/alert-dialog';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Status helpers
const STEP_DISPLAY_NAMES = {
  bedroom_ready: 'Bedroom Ready',
  occupied: 'Occupied',
  need_to_be_clean: 'Need to be Clean',
  mtc_inspection: 'MTC Inspection',
  pmo_inspection: 'PMO Inspection',
  progress_cleaning: 'Progress Cleaning',
  verbed_linen: 'Verbed Linen',
  final_checking: 'Final Checking'
};

// Short names for mobile
const STEP_SHORT_NAMES = {
  bedroom_ready: 'Ready',
  occupied: 'Occupied',
  need_to_be_clean: 'Need Clean',
  mtc_inspection: 'MTC',
  pmo_inspection: 'PMO',
  progress_cleaning: 'Cleaning',
  verbed_linen: 'Linen',
  final_checking: 'Final Check'
};

const STATUS_COLORS = {
  bedroom_ready: 'bg-green-100 text-green-800',
  occupied: 'bg-red-100 text-red-800',
  need_to_be_clean: 'bg-amber-100 text-amber-800',
  mtc_inspection: 'bg-blue-100 text-blue-800',
  pmo_inspection: 'bg-purple-100 text-purple-800',
  progress_cleaning: 'bg-teal-100 text-teal-800',
  verbed_linen: 'bg-emerald-100 text-emerald-800',
  final_checking: 'bg-sky-100 text-sky-800'
};

const STATUS_STRIP_COLORS = {
  bedroom_ready: 'bg-green-500',
  occupied: 'bg-red-500',
  need_to_be_clean: 'bg-amber-500',
  mtc_inspection: 'bg-blue-500',
  pmo_inspection: 'bg-purple-500',
  progress_cleaning: 'bg-teal-500',
  verbed_linen: 'bg-emerald-500',
  final_checking: 'bg-sky-500'
};

const WORKFLOW_STEPS = ['need_to_be_clean', 'mtc_inspection', 'pmo_inspection', 'progress_cleaning', 'verbed_linen', 'final_checking', 'bedroom_ready'];

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/api/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await api.post('/api/auth/register', data);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="spinner w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full"></div></div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

// Login Page - Responsive
function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="login-container" data-testid="login-page">
      {/* Hero Section - Hidden on mobile */}
      <div className="login-hero" style={{backgroundImage: 'url(https://images.unsplash.com/photo-1651038896955-db7785b1eb63?auto=format&fit=crop&q=85)'}}>
        <div className="absolute inset-0 bg-gradient-to-br from-sky-900/80 to-teal-900/80"></div>
        <div className="relative z-10 p-8 lg:p-12 flex flex-col justify-between h-full">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white font-manrope">Ward Occupancy</h1>
            <p className="text-sky-100 mt-2">Flow Tracking System</p>
          </div>
          <div className="text-white/80 text-sm">
            <p>JCI 7th Edition Compliant</p>
            <p>FMS & IPSG Standards</p>
          </div>
        </div>
      </div>
      
      {/* Login Form */}
      <div className="login-form-container bg-slate-50">
        <Card className="login-card">
          <CardHeader className="space-y-1 pb-4">
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-4">
              <h1 className="text-2xl font-bold text-sky-700 font-manrope">Ward Occupancy</h1>
              <p className="text-sm text-slate-500">Flow Tracking System</p>
            </div>
            <CardTitle className="text-xl sm:text-2xl font-manrope">Sign In</CardTitle>
            <p className="text-slate-500 text-sm">Enter your credentials to access the system</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  placeholder="you@hospital.com" 
                  required 
                  className="h-11 text-base"
                  data-testid="login-email" 
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input 
                  id="password" 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  placeholder="••••••••" 
                  required 
                  className="h-11 text-base"
                  data-testid="login-password" 
                />
              </div>
              <Button type="submit" className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-base" disabled={loading} data-testid="login-submit">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            <Separator className="my-6" />
            <p className="text-sm text-slate-500 text-center">Need an account? Contact your administrator.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Navigation - Already responsive with Sheet
function Navigation({ sidebarOpen, setSidebarOpen }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/wards', label: 'Wards & Beds', icon: Bed },
    { path: '/master-data', label: 'Master Data', icon: Building2 },
    { path: '/audit-logs', label: 'Audit Trail', icon: ClipboardList },
    { path: '/reports', label: 'Reports', icon: FileText },
  ];

  if (user?.role === 'admin') {
    navItems.push({ path: '/users', label: 'Users', icon: Users });
  }

  const NavContent = () => (
    <div className="flex flex-col h-full safe-area-top">
      <div className="p-4 border-b">
        <h2 className="text-lg font-bold font-manrope text-sky-700">Ward Occupancy</h2>
        <p className="text-xs text-slate-500">Flow Tracking System</p>
      </div>
      <ScrollArea className="flex-1 p-3">
        <nav className="space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">{item.label}</span>
            </Link>
          ))}
        </nav>
      </ScrollArea>
      <div className="p-4 border-t safe-area-bottom">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-700 font-semibold flex-shrink-0">
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
          </div>
        </div>
        <Button variant="outline" className="w-full h-10" onClick={() => { logout(); navigate('/login'); }} data-testid="logout-button">
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile header */}
      <div className="lg:hidden header px-3 py-2.5 flex items-center justify-between safe-area-top">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="h-10 w-10" data-testid="mobile-menu-button">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 max-w-[85vw]">
            <NavContent />
          </SheetContent>
        </Sheet>
        <h1 className="font-bold font-manrope text-sky-700 text-base">Ward Occupancy</h1>
        <div className="w-10" />
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r">
        <NavContent />
      </aside>
    </>
  );
}

// Dashboard Page - Responsive
function DashboardPage() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cycles, setCycles] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, cyclesRes] = await Promise.all([
        api.get('/api/report/dashboard'),
        api.get('/api/turnover/list?limit=10')
      ]);
      setDashboard(dashRes.data);
      setCycles(cyclesRes.data.cycles);
    } catch (err) {
      toast.error('Failed to load dashboard');
    }
    setLoading(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full"></div></div>;

  const formatTat = (minutes) => {
    if (!minutes) return '--';
    if (minutes < 60) return `${minutes.toFixed(2)}m`;
    return `${(minutes / 60).toFixed(2)}h`;
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-manrope">Dashboard</h1>
          <p className="text-slate-500 text-sm">Real-time ward occupancy overview</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="h-9" data-testid="dashboard-refresh">
          <RefreshCw className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* KPI Cards - Responsive Grid */}
      <div className="grid-responsive-4">
        <Card className="kpi-card" data-testid="kpi-total-beds">
          <p className="text-xs uppercase tracking-wide text-slate-500">Total Beds</p>
          <p className="mt-1.5 text-2xl sm:text-3xl font-bold text-sky-700">{dashboard?.total_beds || 0}</p>
        </Card>
        <Card className="kpi-card" data-testid="kpi-active-cycles">
          <p className="text-xs uppercase tracking-wide text-slate-500">Active Turnovers</p>
          <p className="mt-1.5 text-2xl sm:text-3xl font-bold text-amber-600">{dashboard?.active_cycles || 0}</p>
        </Card>
        <Card className="kpi-card" data-testid="kpi-completed-today">
          <p className="text-xs uppercase tracking-wide text-slate-500">Completed Today</p>
          <p className="mt-1.5 text-2xl sm:text-3xl font-bold text-green-600">{dashboard?.completed_today || 0}</p>
        </Card>
        <Card className="kpi-card" data-testid="kpi-avg-tat">
          <p className="text-xs uppercase tracking-wide text-slate-500">Avg Total TAT</p>
          <p className="mt-1.5 text-2xl sm:text-3xl font-bold text-teal-600">{formatTat(dashboard?.avg_tat?.avg_total)}</p>
        </Card>
      </div>

      {/* Status Distribution - Responsive */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg">Beds by Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="status-grid">
            {Object.entries(dashboard?.status_counts || {}).map(([status, count]) => (
              <div key={status} className="text-center p-2 sm:p-3 rounded-lg bg-slate-50">
                <Badge className={`${STATUS_COLORS[status] || 'bg-slate-100'} text-xs`}>
                  <span className="hidden sm:inline">{STEP_DISPLAY_NAMES[status] || status}</span>
                  <span className="sm:hidden">{STEP_SHORT_NAMES[status] || status}</span>
                </Badge>
                <p className="mt-1.5 text-xl sm:text-2xl font-bold">{count}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Average TAT by Step - Responsive */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg">Average TAT by Step</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="tat-grid">
            {[
              { label: 'MTC', fullLabel: 'MTC Inspection', value: dashboard?.avg_tat?.avg_s1 },
              { label: 'PMO', fullLabel: 'PMO Inspection', value: dashboard?.avg_tat?.avg_s2 },
              { label: 'Cleaning', fullLabel: 'Progress Cleaning', value: dashboard?.avg_tat?.avg_s3 },
              { label: 'Linen', fullLabel: 'Verbed Linen', value: dashboard?.avg_tat?.avg_s4 },
              { label: 'Final', fullLabel: 'Final Checking', value: dashboard?.avg_tat?.avg_s5 },
              { label: 'Ready', fullLabel: 'Bedroom Ready', value: dashboard?.avg_tat?.avg_s6 },
            ].map((item, i) => (
              <div key={i} className="text-center p-2 bg-slate-50 rounded-lg">
                <p className="text-xs sm:text-sm text-slate-500">
                  <span className="sm:hidden">{item.label}</span>
                  <span className="hidden sm:inline">{item.fullLabel}</span>
                </p>
                <p className="text-lg sm:text-xl font-semibold mt-1">{formatTat(item.value)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Cycles - Responsive Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg">Recent Turnover Cycles</CardTitle>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          <p className="scroll-hint px-4">← Scroll horizontally to see more →</p>
          <div className="table-container">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[80px]">Bed</TableHead>
                  <TableHead className="min-w-[150px]">Location</TableHead>
                  <TableHead className="min-w-[100px]">Status</TableHead>
                  <TableHead className="min-w-[140px]">Started</TableHead>
                  <TableHead className="min-w-[80px]">TAT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cycles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-8">No turnover cycles found</TableCell>
                  </TableRow>
                ) : (
                  cycles.map((cycle) => (
                    <TableRow key={cycle._id}>
                      <TableCell className="font-medium">{cycle.bed?.code}</TableCell>
                      <TableCell className="text-slate-500 text-xs sm:text-sm">
                        {cycle.building?.name} / {cycle.floor?.name} / {cycle.room?.name}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[cycle.finished_at ? 'bedroom_ready' : cycle.current_step]} text-xs`}>
                          {cycle.finished_at ? 'Done' : STEP_SHORT_NAMES[cycle.current_step]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-xs sm:text-sm">
                        {new Date(cycle.started_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-medium">{formatTat(cycle.tat?.total)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Wards & Beds Page - Dropdown Filter View
function WardsPage() {
  const [beds, setBeds] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedFloor, setSelectedFloor] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [selectedBed, setSelectedBed] = useState(null);
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [buildingsRes, floorsRes, roomsRes, bedsRes] = await Promise.all([
        api.get('/api/buildings'),
        api.get('/api/floors'),
        api.get('/api/rooms'),
        api.get('/api/beds')
      ]);
      
      const allBuildings = buildingsRes.data.buildings;
      
      // Filter buildings based on user assignment (if not admin)
      let accessibleBuildings = allBuildings;
      if (user?.role !== 'admin') {
        const assignedBuildingIds = user?.assigned_buildings || [];
        const assignedFloorIds = user?.assigned_floors || [];
        
        // Get building IDs from assigned floors
        const buildingsFromFloors = new Set();
        floorsRes.data.floors.forEach(f => {
          if (assignedFloorIds.includes(f._id)) {
            buildingsFromFloors.add(f.building_id);
          }
        });
        
        // Combine assigned buildings + buildings from assigned floors
        const accessibleBuildingIds = new Set([...assignedBuildingIds, ...buildingsFromFloors]);
        accessibleBuildings = allBuildings.filter(b => accessibleBuildingIds.has(b._id));
      }
      
      setBuildings(accessibleBuildings);
      setFloors(floorsRes.data.floors);
      setRooms(roomsRes.data.rooms);
      setBeds(bedsRes.data.beds);
      
      // Auto-select first building if user has assignments
      if (accessibleBuildings.length > 0 && !selectedBuilding) {
        setSelectedBuilding(accessibleBuildings[0]._id);
      }
    } catch (err) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const handleBedClick = (bed) => {
    setSelectedBed(bed);
    setWorkflowDialogOpen(true);
  };

  // Filtered options based on selection AND user assignment
  const filteredFloors = selectedBuilding 
    ? floors.filter(f => {
        // Filter by selected building
        if (f.building_id !== selectedBuilding) return false;
        
        // If user is admin, show all floors in building
        if (user?.role === 'admin') return true;
        
        // If user has building assigned, they can access all floors in it
        if (user?.assigned_buildings?.includes(selectedBuilding)) return true;
        
        // Otherwise, only show specifically assigned floors
        return user?.assigned_floors?.includes(f._id);
      })
    : [];
  
  const filteredRooms = selectedFloor 
    ? rooms.filter(r => r.floor_id === selectedFloor)
    : [];

  // Get beds for selected room
  const filteredBeds = selectedRoom 
    ? beds.filter(b => b.room_id === selectedRoom)
    : [];

  // Get selected room info
  const selectedRoomInfo = rooms.find(r => r._id === selectedRoom);
  const selectedFloorInfo = floors.find(f => f._id === selectedFloor);
  const selectedBuildingInfo = buildings.find(b => b._id === selectedBuilding);

  // Count beds by status
  const countBedStatus = (bedList) => {
    const counts = { available: 0, active: 0 };
    bedList.forEach(b => {
      if (b.current_status === 'bedroom_ready') {
        counts.available++;
      } else if (b.current_status === 'occupied') {
        // Occupied - not available and not in workflow yet
      } else {
        // In turnover workflow (need_to_be_clean through final_checking)
        counts.active++;
      }
    });
    return counts;
  };

  const roomStatus = countBedStatus(filteredBeds);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full"></div></div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="wards-page">
      <div className="page-header">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-manrope">Wards & Beds</h1>
          <p className="text-slate-500 text-sm">Manage bed turnover workflow</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="h-9">
          <RefreshCw className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Building Filter */}
            <div>
              <Label className="text-xs mb-1.5 block">Building</Label>
              <Select 
                value={selectedBuilding} 
                onValueChange={(v) => { 
                  setSelectedBuilding(v === 'none' ? '' : v); 
                  setSelectedFloor(''); 
                  setSelectedRoom(''); 
                }}
              >
                <SelectTrigger className="h-10" data-testid="filter-building">
                  <SelectValue placeholder="Select Building" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select Building --</SelectItem>
                  {buildings.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Floor Filter */}
            <div>
              <Label className="text-xs mb-1.5 block">Floor</Label>
              <Select 
                value={selectedFloor} 
                onValueChange={(v) => { 
                  setSelectedFloor(v === 'none' ? '' : v); 
                  setSelectedRoom(''); 
                }} 
                disabled={!selectedBuilding}
              >
                <SelectTrigger className="h-10" data-testid="filter-floor">
                  <SelectValue placeholder={selectedBuilding ? "Select Floor" : "Select Building first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select Floor --</SelectItem>
                  {filteredFloors.map(f => <SelectItem key={f._id} value={f._id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Room/Ward Filter */}
            <div>
              <Label className="text-xs mb-1.5 block">Room / Ward</Label>
              <Select 
                value={selectedRoom} 
                onValueChange={(v) => setSelectedRoom(v === 'none' ? '' : v)} 
                disabled={!selectedFloor}
              >
                <SelectTrigger className="h-10" data-testid="filter-room">
                  <SelectValue placeholder={selectedFloor ? "Select Room" : "Select Floor first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select Room --</SelectItem>
                  {filteredRooms.map(r => (
                    <SelectItem key={r._id} value={r._id}>
                      {r.name} ({beds.filter(b => b.room_id === r._id).length} beds)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Display Area */}
      {!selectedBuilding ? (
        <Card className="p-8 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-600">Select a Building</p>
          <p className="text-sm text-slate-500 mt-1">Choose a building from the filter above to view floors and rooms.</p>
        </Card>
      ) : !selectedFloor ? (
        <Card className="p-8 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-600">Select a Floor</p>
          <p className="text-sm text-slate-500 mt-1">Choose a floor from the filter to view rooms.</p>
          {filteredFloors.length === 0 && (
            <p className="text-sm text-amber-600 mt-2">No floors in this building. Add floors in Master Data.</p>
          )}
        </Card>
      ) : !selectedRoom ? (
        <Card className="p-8 text-center">
          <Bed className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-600">Select a Room / Ward</p>
          <p className="text-sm text-slate-500 mt-1">Choose a room from the filter to view beds.</p>
          {filteredRooms.length === 0 && (
            <p className="text-sm text-amber-600 mt-2">No rooms on this floor. Add rooms in Master Data.</p>
          )}
        </Card>
      ) : (
        <>
          {/* Room Header */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">{selectedRoomInfo?.name}</h2>
                  <p className="text-sm text-slate-500">
                    {selectedBuildingInfo?.name} / {selectedFloorInfo?.name}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-center px-3">
                    <p className="text-2xl font-bold text-sky-700">{filteredBeds.length}</p>
                    <p className="text-xs text-slate-500">Total Beds</p>
                  </div>
                  <div className="text-center px-3 border-l">
                    <p className="text-2xl font-bold text-green-600">{roomStatus.available}</p>
                    <p className="text-xs text-slate-500">Available</p>
                  </div>
                  <div className="text-center px-3 border-l">
                    <p className="text-2xl font-bold text-amber-600">{roomStatus.active}</p>
                    <p className="text-xs text-slate-500">In Progress</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Beds Grid */}
          {filteredBeds.length === 0 ? (
            <Card className="p-8 text-center">
              <Bed className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-600">No beds in this room</p>
              <p className="text-sm text-slate-500 mt-1">Add beds in Master Data.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {filteredBeds.map(bed => (
                <div
                  key={bed._id}
                  className="bed-tile cursor-pointer no-select"
                  onClick={() => handleBedClick(bed)}
                  data-testid={`bed-tile-${bed.code}`}
                >
                  <div className={`bed-tile-status-strip ${STATUS_STRIP_COLORS[bed.current_status] || 'bg-slate-300'}`} />
                  <div className="bed-tile-content">
                    <p className="bed-tile-code">{bed.code}</p>
                    <Badge className={`mt-1.5 text-xs ${STATUS_COLORS[bed.current_status] || 'bg-slate-100'}`}>
                      {STEP_SHORT_NAMES[bed.current_status] || bed.current_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Workflow Dialog */}
      {selectedBed && (
        <WorkflowDialog 
          bed={selectedBed} 
          open={workflowDialogOpen} 
          onClose={() => { setWorkflowDialogOpen(false); setSelectedBed(null); }}
          onUpdate={fetchData}
          user={user}
        />
      )}
    </div>
  );
}

// Workflow Dialog Component - Responsive
function WorkflowDialog({ bed, open, onClose, onUpdate, user }) {
  const [cycle, setCycle] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [checklistResponses, setChecklistResponses] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (open && bed) {
      fetchCycle();
    }
  }, [open, bed]);

  const fetchCycle = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/turnover/active/${bed._id}`);
      setCycle(res.data.cycle);
      if (res.data.cycle) {
        // Load checklist for CURRENT step that needs to be completed
        await fetchChecklist(res.data.cycle.current_step);
      } else {
        await fetchChecklist('need_to_be_clean');
      }
    } catch (err) {
      console.error('Failed to load cycle:', err);
      // Don't show error toast if it's just a "no active cycle" case
      // Still try to load the checklist for starting a new cycle
      await fetchChecklist('need_to_be_clean');
    }
    setLoading(false);
  };

  const fetchChecklist = async (step) => {
    if (!step) return;
    try {
      const res = await api.get(`/api/checklists/${step}`);
      setChecklist(res.data.checklist);
      setChecklistResponses({});
    } catch (err) {
      console.error('Failed to load checklist');
    }
  };

  const getNextStep = (currentStep) => {
    const idx = WORKFLOW_STEPS.indexOf(currentStep);
    if (idx === -1 || idx === WORKFLOW_STEPS.length - 1) return null;
    return WORKFLOW_STEPS[idx + 1];
  };

  const canPerformStep = (step) => {
    const rolePermissions = {
      admin: WORKFLOW_STEPS,
      fms: ['mtc_inspection'],
      arsa_pest: ['pmo_inspection'],
      arsa_cleaning: ['progress_cleaning'],
      ajun: ['verbed_linen'],
      housekeeping: ['final_checking', 'bedroom_ready'],
      nurse: ['need_to_be_clean', 'bedroom_ready']
    };
    return rolePermissions[user?.role]?.includes(step);
  };

  const handleStartTurnover = async () => {
    if (!allRequiredChecked()) {
      toast.error('Please complete all required checklist items');
      return;
    }
    setActionLoading(true);
    try {
      const responses = checklist.map(item => ({
        id: item.id,
        checked: checklistResponses[item.id] || false
      }));
      await api.post('/api/turnover/start', {
        bed_id: bed._id,
        checklist_responses: responses,
        notes: notes
      });
      toast.success('Turnover cycle started!');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start turnover');
    }
    setActionLoading(false);
  };

  const handleCompleteStep = async () => {
    if (!allRequiredChecked()) {
      toast.error('Please complete all required checklist items');
      return;
    }
    const nextStep = getNextStep(cycle.current_step);
    if (!nextStep) return;

    setActionLoading(true);
    try {
      const responses = checklist.map(item => ({
        id: item.id,
        checked: checklistResponses[item.id] || false
      }));
      await api.post(`/api/turnover/${cycle._id}/step`, {
        step_name: nextStep,
        checklist_responses: responses,
        notes: notes
      });
      toast.success(`Step "${STEP_SHORT_NAMES[nextStep]}" completed!`);
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete step');
    }
    setActionLoading(false);
  };

  const allRequiredChecked = () => {
    return checklist.filter(item => item.required).every(item => checklistResponses[item.id]);
  };

  const currentStep = cycle ? cycle.current_step : null;
  const nextStep = currentStep ? getNextStep(currentStep) : 'need_to_be_clean';
  const stepToShow = cycle ? currentStep : 'need_to_be_clean';  // Step we're currently working on
  const canAct = stepToShow && canPerformStep(stepToShow);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-hidden flex flex-col gap-0" data-testid="workflow-dialog">
        <DialogHeader className="flex-shrink-0 pb-3 border-b">
          <DialogTitle className="font-manrope text-lg">
            Bed {bed.code}
          </DialogTitle>
          <p className="text-sm text-slate-500">
            {bed.building?.name} / {bed.floor?.name} / {bed.room?.name}
          </p>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-y-auto">
          <div className="px-1 py-4">
          {loading ? (
            <div className="flex justify-center py-8"><div className="spinner w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full"></div></div>
          ) : (
            <div className="space-y-4">
              {/* Current Status */}
              <div>
                <p className="text-xs text-slate-500 mb-1.5">Current Status</p>
                <Badge className={`${STATUS_COLORS[bed.current_status]} px-3 py-1`}>
                  {STEP_DISPLAY_NAMES[bed.current_status] || bed.current_status}
                </Badge>
              </div>

              {/* Timeline - Horizontal Stepper */}
              {cycle && (
                <div>
                  <p className="text-sm font-medium mb-3">Workflow Timeline</p>
                  <div className="flex items-center justify-between gap-1 overflow-x-auto pb-2">
                    {WORKFLOW_STEPS.map((step, idx) => {
                      const stepTime = cycle.status_times?.[step];
                      const isCompleted = !!stepTime;
                      const isCurrent = cycle.current_step === step;
                      const actorName = cycle.actor_names?.[step];
                      const tatMinutes = cycle.tat?.[`s${idx + 1}`];

                      return (
                        <div key={step} className="flex items-center flex-1 min-w-0">
                          <div className="flex flex-col items-center flex-1">
                            {/* Step Circle */}
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                              isCompleted 
                                ? 'bg-green-500 text-white' 
                                : isCurrent 
                                ? 'bg-sky-500 text-white' 
                                : 'bg-slate-200 text-slate-400'
                            }`}>
                              {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : idx + 1}
                            </div>
                            {/* Step Label */}
                            <p className={`text-xs mt-1.5 text-center font-medium ${
                              isCompleted ? 'text-green-700' : isCurrent ? 'text-sky-700' : 'text-slate-400'
                            }`}>
                              {STEP_SHORT_NAMES[step]}
                            </p>
                            {/* Time */}
                            {stepTime && (
                              <p className="text-[10px] text-slate-400 mt-0.5 text-center truncate max-w-full">
                                {new Date(stepTime).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })}
                              </p>
                            )}
                            {/* TAT Duration */}
                            {isCompleted && tatMinutes !== undefined && tatMinutes !== null && (
                              <p className="text-[10px] font-semibold text-green-600 mt-0.5">
                                {tatMinutes.toFixed(2)}min
                              </p>
                            )}
                          </div>
                          {/* Connector Line */}
                          {idx < WORKFLOW_STEPS.length - 1 && (
                            <div className={`h-0.5 flex-1 mx-1 ${isCompleted ? 'bg-green-500' : 'bg-slate-200'}`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action Section */}
              {stepToShow && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">
                    {cycle ? `Complete: ${STEP_DISPLAY_NAMES[stepToShow]}` : 'Start Turnover'}
                  </p>

                  {!canAct && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                      <p className="text-sm text-amber-800">
                        Your role ({user?.role}) cannot perform this step.
                      </p>
                    </div>
                  )}

                  {canAct && (
                    <>
                      {/* Checklist */}
                      <div className="space-y-1 mb-3">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">JCI Checklist</p>
                        {checklist.map(item => (
                          <div key={item.id} className="checklist-item">
                            <Checkbox
                              id={item.id}
                              checked={checklistResponses[item.id] || false}
                              onCheckedChange={(checked) => setChecklistResponses(prev => ({...prev, [item.id]: checked}))}
                              data-testid={`checklist-${item.id}`}
                            />
                            <Label htmlFor={item.id} className="text-sm cursor-pointer flex-1 leading-tight">
                              {item.text}
                              {item.required && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                          </div>
                        ))}
                      </div>

                      {/* Notes */}
                      <div>
                        <Label htmlFor="notes" className="text-xs">Notes (optional)</Label>
                        <Textarea
                          id="notes"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Add any notes..."
                          className="mt-1 text-base"
                          rows={2}
                          data-testid="workflow-notes"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Completed message */}
              {cycle?.finished_at && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="font-medium text-green-700">Turnover Completed!</p>
                  <p className="text-sm text-green-600">Total TAT: {(cycle.tat?.total || 0).toFixed(2)} minutes</p>
                </div>
              )}
            </div>
          )}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-shrink-0 pt-4 border-t gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} className="flex-1 sm:flex-none h-10">Close</Button>
          {canAct && stepToShow && !cycle?.finished_at && (
            <Button 
              onClick={cycle ? handleCompleteStep : handleStartTurnover}
              disabled={actionLoading || !allRequiredChecked()}
              className="flex-1 sm:flex-none bg-sky-600 hover:bg-sky-700 h-10"
              data-testid="workflow-submit"
            >
              {actionLoading ? 'Processing...' : (cycle ? `Complete ${STEP_SHORT_NAMES[stepToShow]}` : 'Start Turnover')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Master Data Page - Responsive
function MasterDataPage() {
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('buildings');
  const { user } = useAuth();

  // Form states
  const [newBuilding, setNewBuilding] = useState({ name: '', code: '' });
  const [newFloor, setNewFloor] = useState({ building_id: '', number: '', name: '' });
  const [newRoom, setNewRoom] = useState({ building_id: '', floor_id: '', name: '' });
  const [newBed, setNewBed] = useState({ room_id: '', code: '' });

  // Edit states
  const [editItem, setEditItem] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editType, setEditType] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [b, f, r, bedsRes] = await Promise.all([
        api.get('/api/buildings'),
        api.get('/api/floors'),
        api.get('/api/rooms'),
        api.get('/api/beds')
      ]);
      setBuildings(b.data.buildings);
      setFloors(f.data.floors);
      setRooms(r.data.rooms);
      setBeds(bedsRes.data.beds);
    } catch (err) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  // Create handlers
  const handleCreateBuilding = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/buildings', newBuilding);
      toast.success('Building created');
      setNewBuilding({ name: '', code: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create building');
    }
  };

  const handleCreateFloor = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/floors', { ...newFloor, number: parseInt(newFloor.number) });
      toast.success('Floor created');
      setNewFloor({ building_id: '', number: '', name: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create floor');
    }
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/rooms', newRoom);
      toast.success('Room created');
      setNewRoom({ building_id: '', floor_id: '', name: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create room');
    }
  };

  const handleCreateBed = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/beds', newBed);
      toast.success('Bed created');
      setNewBed({ room_id: '', code: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create bed');
    }
  };

  // Edit handlers
  const openEditDialog = (item, type) => {
    setEditItem({ ...item });
    setEditType(type);
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      if (editType === 'building') {
        await api.put(`/api/buildings/${editItem._id}`, { name: editItem.name, code: editItem.code });
      } else if (editType === 'floor') {
        await api.put(`/api/floors/${editItem._id}`, { 
          building_id: editItem.building_id, 
          number: editItem.number, 
          name: editItem.name 
        });
      } else if (editType === 'room') {
        await api.put(`/api/rooms/${editItem._id}`, { 
          building_id: editItem.building_id, 
          floor_id: editItem.floor_id, 
          name: editItem.name 
        });
      } else if (editType === 'bed') {
        await api.put(`/api/beds/${editItem._id}`, { 
          room_id: editItem.room_id, 
          code: editItem.code 
        });
      }
      toast.success(`${editType.charAt(0).toUpperCase() + editType.slice(1)} updated`);
      setEditDialogOpen(false);
      setEditItem(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to update ${editType}`);
    }
  };

  // Delete handlers
  const handleDelete = async (id, type) => {
    try {
      await api.delete(`/api/${type}s/${id}`);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to delete ${type}`);
    }
  };

  const isAdmin = user?.role === 'admin';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full"></div></div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="master-data-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold font-manrope">Master Data</h1>
        <p className="text-slate-500 text-sm">Manage buildings, floors, rooms, and beds</p>
      </div>

      {/* Tabs */}
      <div className="tabs-container border-b -mx-3 px-3 sm:mx-0 sm:px-0">
        <div className="tabs-list">
          {['buildings', 'floors', 'rooms', 'beds'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`tab-button border-b-2 -mb-px capitalize ${tab === t ? 'border-sky-500 text-sky-600 font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Buildings Tab */}
      {tab === 'buildings' && (
        <div className="grid-responsive-2">
          {isAdmin && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Add Building</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleCreateBuilding} className="space-y-3">
                  <div><Label className="text-xs">Name</Label><Input value={newBuilding.name} onChange={e => setNewBuilding({...newBuilding, name: e.target.value})} required className="h-10 mt-1" data-testid="building-name" /></div>
                  <div><Label className="text-xs">Code</Label><Input value={newBuilding.code} onChange={e => setNewBuilding({...newBuilding, code: e.target.value})} required className="h-10 mt-1" data-testid="building-code" /></div>
                  <Button type="submit" className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 h-10" data-testid="building-submit">Add Building</Button>
                </form>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Buildings ({buildings.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {buildings.map(b => (
                  <div key={b._id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{b.name}</p>
                      <p className="text-xs text-slate-500">Code: {b.code}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(b, 'building')}>
                          <Pencil className="h-4 w-4 text-slate-500" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Building?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete "{b.name}" and may affect related floors, rooms, and beds.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(b._id, 'building')}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                ))}
                {buildings.length === 0 && <p className="text-slate-500 text-center py-4 text-sm">No buildings yet</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Floors Tab */}
      {tab === 'floors' && (
        <div className="grid-responsive-2">
          {isAdmin && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Add Floor</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleCreateFloor} className="space-y-3">
                  <div>
                    <Label className="text-xs">Building</Label>
                    <Select value={newFloor.building_id} onValueChange={v => setNewFloor({...newFloor, building_id: v})}>
                      <SelectTrigger className="h-10 mt-1" data-testid="floor-building"><SelectValue placeholder="Select Building" /></SelectTrigger>
                      <SelectContent>{buildings.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Floor Number</Label><Input type="number" value={newFloor.number} onChange={e => setNewFloor({...newFloor, number: e.target.value})} required className="h-10 mt-1" data-testid="floor-number" /></div>
                  <div><Label className="text-xs">Name (optional)</Label><Input value={newFloor.name} onChange={e => setNewFloor({...newFloor, name: e.target.value})} className="h-10 mt-1" data-testid="floor-name" /></div>
                  <Button type="submit" className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 h-10" disabled={!newFloor.building_id} data-testid="floor-submit">Add Floor</Button>
                </form>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Floors ({floors.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {floors.map(f => {
                  const building = buildings.find(b => b._id === f.building_id);
                  return (
                    <div key={f._id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{f.name}</p>
                        <p className="text-xs text-slate-500 truncate">{building?.name}</p>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(f, 'floor')}>
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Floor?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete "{f.name}" and may affect related rooms and beds.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(f._id, 'floor')}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  );
                })}
                {floors.length === 0 && <p className="text-slate-500 text-center py-4 text-sm">No floors yet</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rooms Tab */}
      {tab === 'rooms' && (
        <div className="grid-responsive-2">
          {isAdmin && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Add Room</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleCreateRoom} className="space-y-3">
                  <div>
                    <Label className="text-xs">Building</Label>
                    <Select value={newRoom.building_id} onValueChange={v => setNewRoom({...newRoom, building_id: v, floor_id: ''})}>
                      <SelectTrigger className="h-10 mt-1" data-testid="room-building"><SelectValue placeholder="Select Building" /></SelectTrigger>
                      <SelectContent>{buildings.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Floor</Label>
                    <Select value={newRoom.floor_id} onValueChange={v => setNewRoom({...newRoom, floor_id: v})} disabled={!newRoom.building_id}>
                      <SelectTrigger className="h-10 mt-1" data-testid="room-floor"><SelectValue placeholder="Select Floor" /></SelectTrigger>
                      <SelectContent>{floors.filter(f => f.building_id === newRoom.building_id).map(f => <SelectItem key={f._id} value={f._id}>{f.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Room Name</Label><Input value={newRoom.name} onChange={e => setNewRoom({...newRoom, name: e.target.value})} required className="h-10 mt-1" data-testid="room-name" /></div>
                  <Button type="submit" className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 h-10" disabled={!newRoom.floor_id} data-testid="room-submit">Add Room</Button>
                </form>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Rooms ({rooms.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {rooms.map(r => {
                  const floor = floors.find(f => f._id === r.floor_id);
                  const building = buildings.find(b => b._id === r.building_id);
                  return (
                    <div key={r._id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{r.name}</p>
                        <p className="text-xs text-slate-500 truncate">{building?.name} / {floor?.name}</p>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(r, 'room')}>
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Room?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete "{r.name}" and may affect related beds.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(r._id, 'room')}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  );
                })}
                {rooms.length === 0 && <p className="text-slate-500 text-center py-4 text-sm">No rooms yet</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Beds Tab */}
      {tab === 'beds' && (
        <div className="grid-responsive-2">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Add Bed</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleCreateBed} className="space-y-3">
                <div>
                  <Label className="text-xs">Room</Label>
                  <Select value={newBed.room_id} onValueChange={v => setNewBed({...newBed, room_id: v})}>
                    <SelectTrigger className="h-10 mt-1" data-testid="bed-room"><SelectValue placeholder="Select Room" /></SelectTrigger>
                    <SelectContent>
                      {rooms.map(r => {
                        const building = buildings.find(b => b._id === r.building_id);
                        return <SelectItem key={r._id} value={r._id}>{building?.name} - {r.name}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Bed Code</Label><Input value={newBed.code} onChange={e => setNewBed({...newBed, code: e.target.value})} placeholder="e.g., BED-001" required className="h-10 mt-1" data-testid="bed-code" /></div>
                <Button type="submit" className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 h-10" disabled={!newBed.room_id} data-testid="bed-submit">Add Bed</Button>
              </form>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Beds ({beds.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {beds.map(b => (
                  <div key={b._id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{b.code}</p>
                      <p className="text-xs text-slate-500 truncate">{b.building?.name} / {b.floor?.name} / {b.room?.name}</p>
                    </div>
                    <Badge className={`${STATUS_COLORS[b.current_status]} text-xs flex-shrink-0`}>{STEP_SHORT_NAMES[b.current_status]}</Badge>
                    {isAdmin && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(b, 'bed')}>
                          <Pencil className="h-4 w-4 text-slate-500" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Bed?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will deactivate bed "{b.code}". Active turnover cycles may be affected.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(b._id, 'bed')}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                ))}
                {beds.length === 0 && <p className="text-slate-500 text-center py-4 text-sm">No beds yet</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {editType.charAt(0).toUpperCase() + editType.slice(1)}</DialogTitle>
          </DialogHeader>
          {editItem && (
            <div className="space-y-3">
              {editType === 'building' && (
                <>
                  <div><Label className="text-xs">Name</Label><Input value={editItem.name} onChange={e => setEditItem({...editItem, name: e.target.value})} className="h-10 mt-1" /></div>
                  <div><Label className="text-xs">Code</Label><Input value={editItem.code} onChange={e => setEditItem({...editItem, code: e.target.value})} className="h-10 mt-1" /></div>
                </>
              )}
              {editType === 'floor' && (
                <>
                  <div>
                    <Label className="text-xs">Building</Label>
                    <Select value={editItem.building_id} onValueChange={v => setEditItem({...editItem, building_id: v})}>
                      <SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{buildings.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Floor Number</Label><Input type="number" value={editItem.number} onChange={e => setEditItem({...editItem, number: parseInt(e.target.value)})} className="h-10 mt-1" /></div>
                  <div><Label className="text-xs">Name</Label><Input value={editItem.name} onChange={e => setEditItem({...editItem, name: e.target.value})} className="h-10 mt-1" /></div>
                </>
              )}
              {editType === 'room' && (
                <>
                  <div>
                    <Label className="text-xs">Building</Label>
                    <Select value={editItem.building_id} onValueChange={v => setEditItem({...editItem, building_id: v, floor_id: ''})}>
                      <SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{buildings.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Floor</Label>
                    <Select value={editItem.floor_id} onValueChange={v => setEditItem({...editItem, floor_id: v})}>
                      <SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{floors.filter(f => f.building_id === editItem.building_id).map(f => <SelectItem key={f._id} value={f._id}>{f.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Room Name</Label><Input value={editItem.name} onChange={e => setEditItem({...editItem, name: e.target.value})} className="h-10 mt-1" /></div>
                </>
              )}
              {editType === 'bed' && (
                <>
                  <div>
                    <Label className="text-xs">Room</Label>
                    <Select value={editItem.room_id} onValueChange={v => setEditItem({...editItem, room_id: v})}>
                      <SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {rooms.map(r => {
                          const building = buildings.find(b => b._id === r.building_id);
                          return <SelectItem key={r._id} value={r._id}>{building?.name} - {r.name}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Bed Code</Label><Input value={editItem.code} onChange={e => setEditItem({...editItem, code: e.target.value})} className="h-10 mt-1" /></div>
                </>
              )}
            </div>
          )}
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Audit Logs Page - Responsive
function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const res = await api.get('/api/audit-logs?limit=200');
      setLogs(res.data.logs);
    } catch (err) {
      toast.error('Failed to load audit logs');
    }
    setLoading(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full"></div></div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="audit-logs-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold font-manrope">Audit Trail</h1>
        <p className="text-slate-500 text-sm">Track all system actions for compliance</p>
      </div>

      <Card>
        <CardContent className="pt-4 px-0 sm:px-6">
          <p className="scroll-hint px-4">← Scroll horizontally to see more →</p>
          <div className="table-container">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[130px]">Timestamp</TableHead>
                  <TableHead className="min-w-[100px]">User</TableHead>
                  <TableHead className="min-w-[80px]">Entity</TableHead>
                  <TableHead className="min-w-[80px]">Action</TableHead>
                  <TableHead className="min-w-[150px]">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-8">No audit logs found</TableCell>
                  </TableRow>
                ) : (
                  logs.map(log => (
                    <TableRow key={log._id}>
                      <TableCell className="text-xs sm:text-sm">{new Date(log.timestamp).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}</TableCell>
                      <TableCell className="font-medium text-sm">{log.user_name}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{log.entity_type}</Badge></TableCell>
                      <TableCell className="text-sm">{log.action}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">
                        {JSON.stringify(log.details)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Reports Page - Responsive
function ReportsPage() {
  const [cycles, setCycles] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    building_id: '',
    status: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cyclesRes, buildingsRes] = await Promise.all([
        api.get('/api/turnover/list?limit=500'),
        api.get('/api/buildings')
      ]);
      setCycles(cyclesRes.data.cycles);
      setBuildings(buildingsRes.data.buildings);
    } catch (err) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.post('/api/report/export', filters, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ward_occupancy_report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report exported!');
    } catch (err) {
      toast.error('Failed to export report');
    }
    setExporting(false);
  };

  const filteredCycles = cycles.filter(c => {
    if (filters.building_id && c.building?._id !== filters.building_id) return false;
    if (filters.status === 'active' && c.finished_at) return false;
    if (filters.status === 'completed' && !c.finished_at) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full"></div></div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="reports-page">
      <div className="page-header">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-manrope">Reports</h1>
          <p className="text-slate-500 text-sm">View and export turnover data</p>
        </div>
        <Button onClick={handleExport} disabled={exporting} className="bg-sky-600 hover:bg-sky-700 h-10" data-testid="export-excel-button">
          <Download className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Export Excel'}</span>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="filter-row">
            <div className="filter-item">
              <Label className="text-xs mb-1.5 block">Building</Label>
              <Select value={filters.building_id} onValueChange={v => setFilters({...filters, building_id: v === 'all' ? '' : v})}>
                <SelectTrigger className="h-10"><SelectValue placeholder="All Buildings" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Buildings</SelectItem>
                  {buildings.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="filter-item">
              <Label className="text-xs mb-1.5 block">Status</Label>
              <Select value={filters.status} onValueChange={v => setFilters({...filters, status: v === 'all' ? '' : v})}>
                <SelectTrigger className="h-10"><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Turnover Cycles ({filteredCycles.length})</CardTitle>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          <p className="scroll-hint px-4">← Scroll horizontally to see more →</p>
          <div className="table-container">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[80px]">Bed</TableHead>
                  <TableHead className="min-w-[150px]">Location</TableHead>
                  <TableHead className="min-w-[90px]">Status</TableHead>
                  <TableHead className="min-w-[100px]">Started</TableHead>
                  <TableHead className="min-w-[100px]">Completed</TableHead>
                  <TableHead className="min-w-[70px]">TAT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCycles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 py-8">No cycles found</TableCell>
                  </TableRow>
                ) : (
                  filteredCycles.map(cycle => (
                    <TableRow key={cycle._id}>
                      <TableCell className="font-medium text-sm">{cycle.bed?.code}</TableCell>
                      <TableCell className="text-slate-500 text-xs">
                        {cycle.building?.name} / {cycle.floor?.name} / {cycle.room?.name}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[cycle.finished_at ? 'bedroom_ready' : cycle.current_step]} text-xs`}>
                          {cycle.finished_at ? 'Done' : STEP_SHORT_NAMES[cycle.current_step]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{new Date(cycle.started_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-xs">{cycle.finished_at ? new Date(cycle.finished_at).toLocaleDateString() : '-'}</TableCell>
                      <TableCell className="font-medium">{cycle.tat?.total ? `${Math.round(cycle.tat.total)}m` : '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Users Page (Admin only) - Responsive
function UsersPage() {
  const [users, setUsers] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [newUser, setNewUser] = useState({ 
    email: '', 
    password: '', 
    name: '', 
    role: 'nurse',
    assigned_buildings: [],
    assigned_floors: []
  });
  const { user, register } = useAuth();

  useEffect(() => {
    fetchUsers();
    fetchBuildingsAndFloors();
  }, []);

  const fetchBuildingsAndFloors = async () => {
    try {
      const [buildingsRes, floorsRes] = await Promise.all([
        api.get('/api/buildings'),
        api.get('/api/floors')
      ]);
      setBuildings(buildingsRes.data.buildings);
      setFloors(floorsRes.data.floors);
    } catch (err) {
      console.error('Failed to load buildings/floors');
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/api/users');
      setUsers(res.data.users);
    } catch (err) {
      toast.error('Failed to load users');
    }
    setLoading(false);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await register(newUser);
      toast.success('User created!');
      setDialogOpen(false);
      setNewUser({ email: '', password: '', name: '', role: 'nurse' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/users/${editUser._id}`, editUser);
      toast.success('User updated!');
      setDialogOpen(false);
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/api/users/${userId}`);
      toast.success('User deleted!');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditDialog = (userToEdit) => {
    setEditUser({ 
      ...userToEdit, 
      password: '',
      assigned_buildings: userToEdit.assigned_buildings || [],
      assigned_floors: userToEdit.assigned_floors || []
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditUser(null);
    setNewUser({ email: '', password: '', name: '', role: 'nurse', assigned_buildings: [], assigned_floors: [] });
  };

  if (user?.role !== 'admin') {
    return <div className="text-center py-12 text-slate-500">Admin access required</div>;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full"></div></div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="users-page">
      <div className="page-header">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-manrope">Users</h1>
          <p className="text-slate-500 text-sm">Manage system users and roles</p>
        </div>
        <Button 
          onClick={() => setDialogOpen(true)} 
          className="bg-sky-600 hover:bg-sky-700 h-10" 
          data-testid="add-user-button"
        >
          <Plus className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Add User</span>
        </Button>
      </div>

      <Card>
        <CardContent className="pt-4 px-0 sm:px-6">
          <p className="scroll-hint px-4">← Scroll horizontally to see more →</p>
          <div className="table-container">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[120px]">Name</TableHead>
                  <TableHead className="min-w-[150px]">Email</TableHead>
                  <TableHead className="min-w-[80px]">Role</TableHead>
                  <TableHead className="min-w-[90px]">Created</TableHead>
                  <TableHead className="min-w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u._id}>
                    <TableCell className="font-medium text-sm">{u.name || u.email.split('@')[0]}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize text-xs">{u.role}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(u)}
                          className="h-8 w-8 p-0"
                          data-testid={`edit-user-${u._id}`}
                        >
                          <Pencil className="w-4 h-4 text-sky-600" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              data-testid={`delete-user-${u._id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete User?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete user "{u.name || u.email}". This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDeleteUser(u._id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      
      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{editUser ? 'Edit User' : 'Add New User'}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 overflow-y-auto pr-4">
          <form onSubmit={editUser ? handleUpdateUser : handleCreateUser} className="space-y-4">
            <div><Label className="text-xs">Name</Label><Input value={editUser ? editUser.name : newUser.name} onChange={e => editUser ? setEditUser({...editUser, name: e.target.value}) : setNewUser({...newUser, name: e.target.value})} required className="h-10 mt-1" data-testid="user-name" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={editUser ? editUser.email : newUser.email} onChange={e => editUser ? setEditUser({...editUser, email: e.target.value}) : setNewUser({...newUser, email: e.target.value})} required className="h-10 mt-1" data-testid="user-email" /></div>
            <div><Label className="text-xs">Password {editUser && <span className="text-slate-400">(leave empty to keep current)</span>}</Label><Input type="password" value={editUser ? editUser.password : newUser.password} onChange={e => editUser ? setEditUser({...editUser, password: e.target.value}) : setNewUser({...newUser, password: e.target.value})} required={!editUser} className="h-10 mt-1" data-testid="user-password" /></div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={editUser ? editUser.role : newUser.role} onValueChange={v => editUser ? setEditUser({...editUser, role: v}) : setNewUser({...newUser, role: v})}>
                <SelectTrigger className="h-10 mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="fms">FMS Team</SelectItem>
                  <SelectItem value="arsa_pest">Arsa Pest Control</SelectItem>
                  <SelectItem value="arsa_cleaning">Arsa Cleaning</SelectItem>
                  <SelectItem value="ajun">Ajun (Linen)</SelectItem>
                  <SelectItem value="housekeeping">Housekeeping</SelectItem>
                  <SelectItem value="nurse">Nurse</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Building & Floor Assignment - Only show for non-admin */}
            {(editUser ? editUser.role : newUser.role) !== 'admin' && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Location Access</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Buildings Column */}
                  <div>
                    <Label className="text-xs font-semibold">Assigned Buildings</Label>
                    <p className="text-[10px] text-slate-400 mb-2">Select buildings user can access</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto border rounded-md p-3 bg-slate-50">
                      {buildings.map(b => (
                        <div key={b._id} className="flex items-center gap-2">
                          <Checkbox
                            id={`building-${b._id}`}
                            checked={(editUser ? editUser.assigned_buildings : newUser.assigned_buildings)?.includes(b._id)}
                            onCheckedChange={(checked) => {
                              const current = editUser ? editUser.assigned_buildings : newUser.assigned_buildings;
                              const updated = checked 
                                ? [...(current || []), b._id]
                                : (current || []).filter(id => id !== b._id);
                              editUser ? setEditUser({...editUser, assigned_buildings: updated}) : setNewUser({...newUser, assigned_buildings: updated});
                            }}
                          />
                          <Label htmlFor={`building-${b._id}`} className="text-xs cursor-pointer flex-1 leading-tight">{b.name}</Label>
                        </div>
                      ))}
                      {buildings.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No buildings available</p>}
                    </div>
                  </div>
                  
                  {/* Floors Column */}
                  <div>
                    <Label className="text-xs font-semibold">Assigned Floors</Label>
                    <p className="text-[10px] text-slate-400 mb-2">Select specific floors (optional)</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto border rounded-md p-3 bg-slate-50">
                      {floors.map(f => {
                        const building = buildings.find(b => b._id === f.building_id);
                        return (
                          <div key={f._id} className="flex items-center gap-2">
                            <Checkbox
                              id={`floor-${f._id}`}
                              checked={(editUser ? editUser.assigned_floors : newUser.assigned_floors)?.includes(f._id)}
                              onCheckedChange={(checked) => {
                                const current = editUser ? editUser.assigned_floors : newUser.assigned_floors;
                                const updated = checked 
                                  ? [...(current || []), f._id]
                                  : (current || []).filter(id => id !== f._id);
                                editUser ? setEditUser({...editUser, assigned_floors: updated}) : setNewUser({...newUser, assigned_floors: updated});
                              }}
                            />
                            <Label htmlFor={`floor-${f._id}`} className="text-xs cursor-pointer flex-1 leading-tight">
                              {f.name} <span className="text-[10px] text-slate-400">({building?.code || building?.name})</span>
                            </Label>
                          </div>
                        );
                      })}
                      {floors.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No floors available</p>}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2">💡 Floors from assigned buildings are automatically included</p>
                  </div>
                </div>
              </div>
            )}
            
            {(editUser ? editUser.role : newUser.role) === 'admin' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-700">✓ Admin has access to all buildings and floors</p>
              </div>
            )}
            
            <DialogFooter className="pt-2">
              <Button type="submit" className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 h-10" data-testid="user-submit">
                {editUser ? 'Update User' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main App Layout
function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="app-container">
      <Navigation sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <main className="lg:pl-64">
        <div className="main-content safe-area-bottom">
          {children}
        </div>
      </main>
    </div>
  );
}

// App Component
function App() {
  return (
    <Router>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
          <Route path="/wards" element={<ProtectedRoute><AppLayout><WardsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/master-data" element={<ProtectedRoute><AppLayout><MasterDataPage /></AppLayout></ProtectedRoute>} />
          <Route path="/audit-logs" element={<ProtectedRoute><AppLayout><AuditLogsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><AppLayout><ReportsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><AppLayout><UsersPage /></AppLayout></ProtectedRoute>} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
